   import { RSAHelper } from 'src/app/_helpers/RSAHelper';
   import html2canvas from 'html2canvas';
   import { jsPDF } from 'jspdf';

   /** Branding for the generated PDF: a logo + document title in the header and text in the footer. */
   export interface PdfBranding {
       /** Header logo image URL or data-URI. Defaults to the ExpenseOnDemand logo. */
       logoUrl?: string;
       /** Document title shown in the header (e.g. "Purchase Order"). */
       title?: string;
       /** Footer text shown centred on every page. */
       footerText?: string;
   }

   /**
    * Renders an on-screen element to a multi-page A4 PDF (data-URI) with a branded
    * letterhead: a logo header and a footer (divider + text + page number) on every
    * page, and the element screenshot as the body. Header/footer bands are reserved so
    * the body never overlaps them; the body is sliced per page for clean pagination.
    */
   export async function CaptureElementToPdfDataUri(element: HTMLElement, branding: PdfBranding = {}): Promise<string | null> {
       if (!element) {
           return null;
       }
       try {
           const logoUrl = branding.logoUrl ?? 'assets/images/logo.png';
           const footerText = branding.footerText
               ?? `©2003-${new Date().getFullYear()} Expense On Demand Ltd (EOD) | All rights reserved. Various trademarks held by their respective owners.`;

           const canvas = await html2canvas(element, { scale: 1.5, useCORS: true, backgroundColor: '#ffffff' });
           // Guard against a zero-size capture (hidden/unrendered element): a 0-width canvas would
           // otherwise produce a runaway page count below.
           if (!canvas.width || !canvas.height) {
               return null;
           }
           const logoImg = await LoadImage(logoUrl);

           const pdf = new jsPDF('p', 'pt', 'a4');
           const pageWidth = pdf.internal.pageSize.getWidth();
           const pageHeight = pdf.internal.pageSize.getHeight();

           const headerHeight = 56;   // reserved top band for the logo
           const footerHeight = 34;   // reserved bottom band for the footer
           const contentHeight = pageHeight - headerHeight - footerHeight;

           // Source pixels that map to one point of content width, and to one page of content height.
           const pxPerPoint = canvas.width / pageWidth;
           const slicePx = Math.max(1, Math.floor(contentHeight * pxPerPoint));
           const totalPages = Math.max(1, Math.ceil(canvas.height / slicePx));

           for (let page = 0; page < totalPages; page++) {
               if (page > 0) {
                   pdf.addPage();
               }

               // Crop the source canvas to this page's slice and place it in the content band.
               const sourceY = page * slicePx;
               const sliceHeight = Math.min(slicePx, canvas.height - sourceY);
               const slice = document.createElement('canvas');
               slice.width = canvas.width;
               slice.height = sliceHeight;
               const sliceCtx = slice.getContext('2d');
               if (!sliceCtx) {
                   continue;
               }
               sliceCtx.drawImage(canvas, 0, sourceY, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
               const drawHeight = sliceHeight / pxPerPoint;
               pdf.addImage(slice.toDataURL('image/jpeg', 0.85), 'JPEG', 0, headerHeight, pageWidth, drawHeight);

               DrawPdfHeader(pdf, logoImg, pageWidth, headerHeight, branding.title);
               DrawPdfFooter(pdf, pageWidth, pageHeight, footerHeight, footerText);
           }

           return pdf.output('datauristring');
       } catch {
           return null;
       }
   }

   /** Loads an image (asset URL or data-URI); resolves to null if it cannot be loaded. */
   function LoadImage(url: string): Promise<HTMLImageElement | null> {
       return new Promise((resolve) => {
           const img = new Image();
           img.crossOrigin = 'anonymous';
           img.onload = () => resolve(img);
           img.onerror = () => resolve(null);
           img.src = url;
       });
   }

   /** Draws the company logo (left) and the document title (right) at the top of the current PDF page. */
   function DrawPdfHeader(pdf: jsPDF, logoImg: HTMLImageElement | null, pageWidth: number, headerHeight: number, title?: string): void {
       if (logoImg && logoImg.naturalWidth > 0) {
           const targetHeight = 26;
           const targetWidth = targetHeight * (logoImg.naturalWidth / logoImg.naturalHeight);
           pdf.addImage(logoImg, 'PNG', 24, (headerHeight - targetHeight) / 2, targetWidth, targetHeight);
       }
       if (title) {
           pdf.setFont('helvetica', 'bold');
           pdf.setFontSize(15);
           pdf.setTextColor(33, 37, 41);
           pdf.text(title, pageWidth - 24, headerHeight / 2 + 4, { align: 'right' });
           pdf.setFont('helvetica', 'normal');
       }
   }

   /** Draws the centred copyright footer text at the bottom of the current PDF page. */
   function DrawPdfFooter(pdf: jsPDF, pageWidth: number, pageHeight: number, footerHeight: number, footerText: string): void {
       const lineY = pageHeight - footerHeight + 2;
       pdf.setDrawColor(226, 232, 240);
       pdf.setLineWidth(0.75);
       pdf.line(24, lineY, pageWidth - 24, lineY);

       pdf.setFontSize(7);
       pdf.setTextColor(120, 120, 120);
       pdf.text(footerText, pageWidth / 2, lineY + 14, { align: 'center' });
   }

   /** Convert a base64 PDF data-URI (e.g. a captured receipt) into a File for upload; null if it can't be parsed. */
   export function fnDataUriToFile(dataUri: string, fileName: string): File | null {
       if (!dataUri) {
           return null;
       }
       try {
           const base64 = dataUri.substring(dataUri.indexOf('base64,') + 'base64,'.length);
           const binary = atob(base64);
           const bytes = new Uint8Array(binary.length);
           for (let i = 0; i < binary.length; i++) {
               bytes[i] = binary.charCodeAt(i);
           }
           return new File([bytes], fileName, { type: 'application/pdf' });
       } catch {
           return null;
       }
   }

   export function CheckIsNumber(value) {
        let isNumber = false;
        if (value && !isNaN(value)) {
            isNumber = true;
        }
        return isNumber;
    }

    export function GetExcelStringValue(value) {
        let stringValue = "";
        if (value && value !== 'N/A') {
            stringValue = value;
        }
        return stringValue;
    }

    export function GetDateValue(date: any) {
        let result = null;
        if (date != null && date != "") {
            var convertedDate = new Date(date);
            let isValid = this.isValidDate(convertedDate);
            if (isValid) {
                result = new Date(date);
            }
        }
        return result;
    }

    export function isValidDate(date) {
        return date && Object.prototype.toString.call(date) === "[object Date]" && !isNaN(date);
    }


    export function GroupBy(objectArray, property) {
        return objectArray.reduce((acc, obj) => {
           const key = obj[property];
           if (!acc[key]) {
              acc[key] = [];
           }
           // Add object to list for given key's value
           acc[key].push(obj);
           return acc;
        }, {});
     }

     export function EncryptParameters(encryptValue) {
        let rsaHelper = new RSAHelper();
        let encrypt = rsaHelper.Encrypt(encryptValue);
        return encrypt;
      }
      
      export function  DecryptParameters(encryptedParam): string {
        let rsaHelper = new RSAHelper();
        let decrypt = rsaHelper.Decrypt(encryptedParam);
        return decrypt;
      }